/**
 * Topic-aware reply layer.
 *
 * Original system was sentiment-only (positive / negative / neutral pool, random
 * pick) — felt like a magic 8-ball because the NPC never acknowledged what the
 * player actually said. This adds a topic-detection step that scans the
 * player's text for keyword clusters and, if it hits, picks from a topic-keyed
 * per-NPC pool. If no topic matches, the caller falls back to its sentiment
 * pool. Result: when you ask the broker about the SEC, the broker says
 * something about the SEC.
 *
 * No LLM. No backend. Deterministic regex.
 */

export type TopicId =
  | 'sec'           // securities-fraud / SEC investigation talk
  | 'irs'           // taxes / audit
  | 'margin'        // leverage / 0DTE / liquidation
  | 'money'         // need money / wire / loan / broke
  | 'trading'       // buy / sell / short / cover (generic order flow)
  | 'crypto'        // bitcoin / wagmi / memecoin
  | 'divorce'       // marriage falling apart
  | 'food'          // eating / dinner / takeout
  | 'meta'          // player breaks 4th wall — nasdaq.one is a sim
  | 'apology'       // i'm sorry, didn't mean it
  | 'thanks'        // thank-you / appreciation
  | 'angry'         // hostile language
  | 'question';     // ends in ? — the player asked something

export interface TopicMatch {
  topic: TopicId;
  // Surface what specifically matched so per-NPC pools can be even more pointed.
  // E.g., "SEC" vs "subpoena" vs "lawsuit". Cheap, optional.
  hit: string;
}

const PATTERNS: { topic: TopicId; pattern: RegExp }[] = [
  // Order matters — earlier patterns win on overlap. Highest-specificity first.
  { topic: 'sec', pattern: /\b(sec|securities[- ]?fraud|cease[- ]?and[- ]?desist|subpoena|investigation|charges?|grand jury|insider trading)\b/i },
  { topic: 'irs', pattern: /\b(irs|tax(es|ing)?|audit|deduction|loophole|offshore|deferred|w-?2|1099)\b/i },
  { topic: 'margin', pattern: /\b(margin|leverage|liquidat(ion|ed?)|maintenance|levered|0dte|options?|calls?|puts?)\b/i },
  { topic: 'money', pattern: /\b(money|cash|loan|broke|borrow|wire|send.*\$|need.*\$|pay.*back|owe|rent|bills?)\b/i },
  { topic: 'trading', pattern: /\b(buy|sell|short|long|cover|exit|enter|position|hold|sold|bought|fill|trade)\b/i },
  { topic: 'crypto', pattern: /\b(crypto|bitcoin|btc|eth|wagmi|ngmi|memecoin|jeet|rugs|pump|airdrop|wallet)\b/i },
  { topic: 'divorce', pattern: /\b(divorce|leaving you|cheating|affair|separat(ed|ion)|alimony|custody)\b/i },
  { topic: 'food', pattern: /\b(eat|eaten|dinner|food|hungry|meal|takeout|brisket|soup)\b/i },
  { topic: 'meta', pattern: /\b(simulat(ed|ion)|game|sim|pixel|fake|nasdaq\.one|prompt|llm|bot|npc|magic 8[- ]?ball)\b/i },
  { topic: 'apology', pattern: /\b(sorry|apologi[zs]e|my bad|forgive|didn't mean|didnt mean|my fault)\b/i },
  { topic: 'thanks', pattern: /\b(thanks?|thx|ty|appreciate|grateful|kind of you)\b/i },
  { topic: 'angry', pattern: /\b(fuck|shit|stop|leave me alone|hate|never|cease|piss off|damn)\b/i },
];

export function detectTopic(text: string): TopicMatch | null {
  const lc = text.toLowerCase();
  for (const { topic, pattern } of PATTERNS) {
    const m = pattern.exec(lc);
    if (m) return { topic, hit: m[0] };
  }
  // Last-resort: if the message ends in a question mark, classify as a question
  if (/\?\s*$/.test(text.trim())) return { topic: 'question', hit: '?' };
  return null;
}

/**
 * Per-NPC topic reply pools. When the player's message matches a topic AND the
 * NPC has lines for that topic, we pull from this. Otherwise we fall back to
 * the existing positive / negative / neutral pools in the calling module.
 *
 * Key by NPC threadId / fromNpc. Same NPC ids work for phone + email + Y replies.
 */
export const NPC_TOPIC_REPLIES: Record<string, Partial<Record<TopicId, string[]>>> = {
  // ─── Phone / Email NPCs ───
  broker: {
    sec: [
      "the SEC matter is fine. we have 47 lawyers on it. they have 47 questions. it scales.",
      "as your broker i cannot legally discuss your SEC exposure over text. that's covered in the welcome packet you didn't read.",
      "off the record: SEC is asking for your trade journal. you don't have a trade journal. fix that.",
    ],
    irs: [
      "tax stuff goes through your accountant. you don't have an accountant. fix that.",
      "we don't help with the IRS. we report to the IRS. those are different.",
    ],
    margin: [
      "your margin is fine. for now. don't ask me again today.",
      "you're not on margin. yet. you keep clicking the leverage button. one day it'll be enabled.",
      "if you blow up on margin, the call comes from this number. consider this a preview.",
    ],
    money: [
      "we don't lend to retail. we collect from retail. those are very different verbs.",
      "your account balance is your problem. that's literally in the brokerage agreement.",
    ],
    trading: [
      "place the order yourself. compliance does not allow me to recommend. compliance does allow me to roll my eyes.",
      "we will not tell you what to do. we will tell you what we did, two days later, in a research note.",
    ],
    crypto: [
      "we don't custody crypto. you can wire to an exchange and lose it there yourself.",
      "if you ask me about $JEET one more time i\'m flagging this account.",
    ],
    food: [
      "i don\'t care if you ate. compliance does not, technically, care either.",
    ],
    meta: [
      "if this is a sim, my paycheck still cleared. i\'m fine with that.",
    ],
    question: [
      "great question. the answer is: read your account agreement.",
      "i\'m not allowed to answer that over text. or in person. or ever, really.",
    ],
  },

  lawyer: {
    sec: [
      "do not — repeat, do NOT — communicate with the SEC without me on the line. not even an emoji. not even a thumbs up.",
      "your SEC matter is being handled. by me. at \$1,400/h. you are paying me to type this sentence.",
      "i need you to preserve every text and every Y post relating to the trade. if you delete a single one, you\'ve created an obstruction problem on top of the SEC problem.",
    ],
    irs: [
      "the IRS is a tax matter. i do securities. you also need a tax lawyer. yes that\'s another retainer.",
    ],
    margin: [
      "if you blow up on margin and post about it on Y, i will quit. quietly. by email.",
    ],
    money: [
      "i am paid hourly. i understand financial stress in a clinical sense. now: pay the invoice.",
    ],
    trading: [
      "do not put trade reasoning in writing. ever. not even with me. especially not with me.",
    ],
    divorce: [
      "i don\'t do family law. i can refer. she will charge \$1,800/h. i checked.",
    ],
    apology: [
      "apology not accepted, but noted. invoice still due.",
    ],
    angry: [
      "i charge an additional 30% for hostile communications. that just got expensive.",
    ],
    meta: [
      "if this is a simulation, the bar will be very interested. i remain billable in either reality.",
    ],
    question: [
      "answering that constitutes legal advice. that costs \$1,400.",
    ],
  },

  mom: {
    sec: [
      "sweetie what is the SEC, is that the people who arrest you. should i call cousin david.",
      "i don\'t know what that is but i don\'t like the sound of it. eat something.",
    ],
    irs: [
      "the IRS just sent you a letter at OUR address. why is it at our address.",
      "your father is going to find out about the IRS thing. i can hold him off until thursday.",
    ],
    margin: [
      "i don\'t know what margin is but every time you say it you sound tired.",
    ],
    money: [
      "we can lend you a little. don\'t tell your sister.",
      "i still have your savings bonds from when you were 7. they are worth, i think, $44.",
    ],
    trading: [
      "did you sell that stock yet. the one that goes up. did you sell it. did you sell it at the top.",
    ],
    crypto: [
      "janet\'s son lost everything in crypto. don\'t be janet\'s son.",
    ],
    food: [
      "PLEASE eat. i made the soup. it\'s in the freezer. i can drive it over.",
      "soup is in the freezer. label says \"DO NOT EAT — for sweetie.\" eat it.",
    ],
    apology: [
      "i forgive you sweetie. of course i forgive you. but call your sister.",
    ],
    angry: [
      "i raised you better than this. this is between you and god now.",
    ],
    divorce: [
      "i love her. i loved her first, actually. don\'t be the bad guy in this.",
    ],
    meta: [
      "i don\'t know what a simulation is sweetie but i know YOU are real to me. love mom.",
    ],
    question: [
      "i don\'t know sweetie. ask your father. don\'t ask your father.",
    ],
  },

  wife: {
    sec: [
      "the SEC?? again?? you didn\'t mention this last time was a thing.",
      "if the SEC takes the house, we have a problem. a bigger problem.",
    ],
    money: [
      "we don\'t have it. you know we don\'t have it. why are you asking.",
      "i moved $3K to a separate account. don\'t ask which bank. that\'s the point.",
    ],
    trading: [
      "did you. did you actually. okay. okay.",
      "you said you stopped. you said it on a sunday. it was last sunday.",
    ],
    food: [
      "there\'s leftovers. i\'m not heating them up for you.",
    ],
    apology: [
      "i\'ve heard this apology before. i\'ll know it\'s real when the trading stops.",
    ],
    divorce: [
      "we are not having that conversation by text.",
      "i\'m talking to my friend about a lawyer. just talking.",
    ],
    angry: [
      "i\'m done.",
    ],
    meta: [
      "if this is a simulation it sucks. who designed it. tell them.",
    ],
    question: [
      "you can come home and ask me in person.",
    ],
  },

  best_friend: {
    sec: [
      "bro the SEC?? respect. who else can say that.",
      "the SEC came after my cousin once for like 200 bucks. you\'ll be fine. probably.",
    ],
    margin: [
      "bro you blew up on margin?? nooo bro nooo. wait — did you size up first or after?",
    ],
    money: [
      "bro i don\'t have it. i\'m kinda broke too. solidarity though.",
    ],
    trading: [
      "wait you did WHAT. send a screenshot. NOW.",
    ],
    crypto: [
      "bro $JEET unironically has community. think about it.",
    ],
    divorce: [
      "bro. bro. let\'s grab beers. tonight. i\'m on my way.",
    ],
    apology: [
      "we good bro. always good.",
    ],
    food: [
      "sushi friday still on?",
    ],
    meta: [
      "bro if we\'re NPCs i was the protagonist the whole time.",
    ],
    question: [
      "idk bro just send it.",
    ],
  },

  'saudi-prince': {
    sec: [
      "the SEC is a very small problem in the larger geometry of things. let me know if you would like it to become smaller.",
      "an investigation is a kind of negotiation. negotiations have rooms. rooms have doors.",
    ],
    money: [
      "we can wire whatever you require. the wire will, of course, leave a note.",
    ],
    angry: [
      "your tone is regrettable. our friendship is, currently, paused.",
    ],
    apology: [
      "the apology is accepted. the wire is en route.",
    ],
    meta: [
      "every reality has a Saudi prince in it. you will find this is true everywhere.",
    ],
    question: [
      "the answer is yes. ask again later. the answer will still be yes.",
    ],
  },

  senator: {
    sec: [
      "i don\'t do SEC. i do appropriations. but i know a guy at SEC who does favors for guys at appropriations. worth thinking about.",
    ],
    money: [
      "campaigns are expensive. every dollar is a vote, in a very loose sense of \"vote.\"",
    ],
    trading: [
      "i can\'t comment on a specific trade. i can comment on a general direction. for that we should meet in person. ideally not in DC.",
    ],
    angry: [
      "watch your tone. this is a recorded line. all my lines are recorded. i\'m the recorder.",
    ],
    meta: [
      "even in a simulation, i would be running for re-election.",
    ],
    question: [
      "i\'ll have my staff get back to you. i don\'t have staff. i have one intern. she\'ll get back to you.",
    ],
  },

  intel: {
    sec: [
      "the SEC has its own intel apparatus. it is, frankly, embarrassing.",
    ],
    money: [
      "the channel is funded. the channel is also monitored. the funding will continue regardless.",
    ],
    angry: [
      "this number will be reassigned within the hour. consider the engagement concluded.",
    ],
    meta: [
      "we are aware of the simulation hypothesis. it is one of seven working theories. we do not rank them.",
    ],
    question: [
      "the answer to your question has been classified. the existence of your question has also been classified.",
    ],
  },

  reporter: {
    sec: [
      "are you going on the record about the SEC? this is national-section material.",
      "we already have a draft of the SEC story. i\'m calling for a quote so the lawyers feel better.",
    ],
    money: [
      "going broke makes for a great features piece. i\'m saying this as a friend.",
    ],
    trading: [
      "specifics? on or off the record? both? \"off the record\" means \"on the record but with extra steps.\"",
    ],
    angry: [
      "hostility in writing is a feature, not a bug. i\'m using all of this.",
    ],
    meta: [
      "if this is a sim, the simulator is paying me. i don\'t know who that is. don\'t care.",
    ],
    question: [
      "great question. i\'ll print your question and your name and let the readers handle the answer.",
    ],
  },

  // ─── Email-only NPCs ───
  spam: {
    sec: [ "Per your inquiry: our offshore concierge service makes SEC concerns substantially easier to compartmentalize." ],
    irs: [ "We specialize in IRS-adjacent restructuring. For just $1,200 in setup fees, your tax footprint disappears. Legally adjacent." ],
    money: [ "Excellent — you\'re in a state of financial readiness! Click the link in our previous 14 emails." ],
    angry: [ "AUTO-REPLY: We\'re sorry to see you reply with hostility. We will continue contacting you, more frequently, until your tone improves." ],
    meta: [ "AUTO-REPLY: A simulation is, technically, a customer. We will continue contacting you in all realities." ],
  },

  plaintiff: {
    sec: [ "Plaintiff is in receipt. The SEC matter is, as it happens, our other firm. We will refer you. To us." ],
    money: [ "Plaintiff is in receipt. Your settlement-readiness language has been logged." ],
    angry: [ "Plaintiff is in receipt. Hostility logged as exhibit. Demand will be amended upward." ],
    apology: [ "Plaintiff is in receipt. An apology in writing is equivalent to a partial admission. Logged accordingly." ],
    meta: [ "Plaintiff is in receipt. The simulation defense has been raised in District Court before. It does not work." ],
    question: [ "Plaintiff is in receipt. Counsel does not answer questions before discovery." ],
  },

  irs: {
    money: [ "Per your reply: payment is due. The amount is non-negotiable. The deadline is non-negotiable. Sincerely, the IRS." ],
    apology: [ "Per your reply: apologies are appreciated. They are, however, not deductible." ],
    angry: [ "Per your reply: hostile communications with the Service do not improve outcomes. Your file has been escalated." ],
    meta: [ "Per your reply: the Service collects in all jurisdictions. The simulation is no exception. Sincerely, the IRS." ],
    question: [ "Per your reply: refer to publication 17. The entirety of publication 17. We will not be answering individually." ],
  },

  fan: {
    sec: [ "u r so based for taking on the SEC. fck them. fr 🔥" ],
    money: [ "if u need cash i can venmo $14 i swear. it\'s all i have." ],
    trading: [ "screenshot ur portfolio i won\'t copy i swear i just want to see" ],
    angry: [ "ok i didn\'t mean to upset u 🥺 i\'ll go" ],
    meta: [ "if u r AI i still love u 💯" ],
    question: [ "QUEEN/KING answered me!!! 😭" ],
  },

  hater: {
    sec: [ "the SEC is, finally, doing something useful. for once.", "imagine being you, with the SEC at your door, and texting me about it. unhinged." ],
    money: [ "good. stay broke." ],
    angry: [ "see you in court. see you in hell. probably the same building." ],
    meta: [ "you are NPC behavior even in the simulation that contains this simulation." ],
    question: [ "no." ],
  },

  podcast: {
    sec: [ "the SEC angle is the one we want. would you come on for the 2-hour with the controversial-guests sponsor segment." ],
    money: [ "we don\'t pay guests. we pay producers, lavishly." ],
    angry: [ "we will not be booking. our policy is clear. our sponsor reads, however, are flexible." ],
    meta: [ "we did a 4-hour episode on the simulation hypothesis last week. if this is one, we\'re getting good ratings." ],
    question: [ "great question. we\'ll send a list of pre-approved answers." ],
  },
};

/**
 * Generic per-topic responses for NPCs that don't have a specific entry. So
 * "money" topic from a contact we haven't authored gets a general money line
 * instead of a sentiment-pool magic-8-ball reply.
 */
export const GENERIC_TOPIC_REPLIES: Partial<Record<TopicId, string[]>> = {
  sec: [
    "the SEC again? not engaging with that over text.",
    "we shouldn\'t talk about the SEC matter here. ever.",
  ],
  irs: [
    "the IRS situation is not something to discuss by text.",
    "the IRS doesn\'t go away. just so you know.",
  ],
  margin: [
    "margin is how this kind of thing ends.",
    "blow up on margin once and you stop blowing up on margin. usually.",
  ],
  money: [
    "money is short for everyone right now. i can\'t help.",
    "i\'m not the right person for that. ask someone with money.",
  ],
  trading: [
    "i\'m not your trade buddy. don\'t loop me in on this.",
    "ok. and you\'re telling me because?",
  ],
  crypto: [
    "i\'m not in crypto. there\'s a reason.",
    "every crypto conversation ends the same way. saving us both the time.",
  ],
  divorce: [
    "i\'m so sorry. that\'s heavy. however i can help.",
    "that\'s not something to handle by text.",
  ],
  food: [
    "have you actually eaten today.",
    "go eat something. then come back to this.",
  ],
  meta: [
    "if this is a simulation, the simulation has a lot of typos.",
    "even if we\'re NPCs, i\'m a well-developed one.",
  ],
  apology: [
    "appreciated. genuinely.",
    "it\'s ok. let\'s move on.",
  ],
  thanks: [
    "you got it.",
    "anytime.",
  ],
  angry: [
    "i\'m gonna let that one go. once.",
    "ok. logging off this thread.",
  ],
  question: [
    "i don\'t know. genuinely don\'t know.",
    "good question. no answer.",
  ],
};
